import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { Col, DatePicker, Row, Select, Table, Tag } from 'antd';
import {
  DollarOutlined,
  EyeOutlined,
  PhoneOutlined,
  RollbackOutlined,
  WalletOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {
  COLORS,
  FONT_WEIGHTS,
  TEXT_SIZES,
  paymentTypeOptions,
  paymentStatusOptions,
  paymentMethodOptions,
} from '../utils/constants';
import { debounce } from '../utils/utils';
import { getTransactions } from '../api/requests/transactions';
import { getEventsListLite } from '../api/requests/events';
import { getFacilitiesListLite } from '../api/requests/facility';
import { CustomTable } from '../components/common/CustomTable';
import { StyledSearch } from './UserManagement';
import {
  TransactionDto,
  TransactionLineItemDto,
  TransactionSearchParams,
} from '../types/types';
import { PaymentMethod, PaymentStatus, PaymentType } from '../enums/enums';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Drawer } from '../components/common/Drawer';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const PAGE_SIZE = 8;

const PAYMENT_STATUS_COLOR_MAP: Record<string, string> = {
  Completed: 'green',
  Pending: 'orange',
  Processing: 'geekblue',
  Failed: 'red',
  Refunded: 'blue',
  Cancelled: 'default',
  'Partially Refunded': 'cyan',
};

const LINE_ITEM_STATUS_COLOR_MAP: Record<string, string> = {
  Completed: 'green',
  Pending: 'orange',
  Failed: 'red',
  Cancelled: 'default',
  RefundPending: 'geekblue',
  Refunded: 'blue',
};

const Transactions = () => {
  const [transactions, setTransactions] = useState<TransactionDto[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Stats
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalRefundedAmount, setTotalRefundedAmount] = useState<number>(0);
  const [totalNetAmount, setTotalNetAmount] = useState<number>(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState<number>(0);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionDto | null>(null);

  // Filters
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    PaymentType | undefined
  >();
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<
    PaymentStatus | undefined
  >();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | undefined
  >();
  const [selectedEventId, setSelectedEventId] = useState<number | undefined>();
  const [eventOptions, setEventOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<
    number | undefined
  >();
  const [facilityOptions, setFacilityOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [dateFilter, setDateFilter] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const fetchEvents = async () => {
    try {
      const data = await getEventsListLite();
      if (data.isSuccess && data.value) {
        setEventOptions(
          data.value.map((e) => ({ label: e.eventName, value: e.eventId })),
        );
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const data = await getFacilitiesListLite();
      if (data.isSuccess && data.value) {
        setFacilityOptions(
          data.value.map((f) => ({
            label: f.facilityName,
            value: f.facilityId,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchFacilities();
  }, []);

  const fetchTransactions = useCallback(
    async (params?: { query?: string; page?: number }) => {
      try {
        setLoading(true);
        const searchParams: TransactionSearchParams = {
          PaymentType: selectedPaymentType,
          PaymentStatus: selectedPaymentStatus,
          PaymentMethod: selectedPaymentMethod,
          EventId: selectedEventId,
          FacilityId: selectedFacilityId,
          FromDate: dateFilter?.[0]
            ? dateFilter[0].startOf('day').toISOString()
            : undefined,
          ToDate: dateFilter?.[1]
            ? dateFilter[1].endOf('day').toISOString()
            : undefined,
          SearchQuery: params?.query?.trim() || undefined,
          PageNumber: params?.page ? params.page - 1 : currentPage - 1,
          PageSize: PAGE_SIZE,
        };

        const data = await getTransactions(searchParams);
        if (data.isSuccess) {
          const value = data.value;
          setTransactions(value?.rows || []);
          setTotalCount(value?.totalCount || 0);
          setTotalAmount(value?.totalAmount || 0);
          setTotalRefundedAmount(value?.totalRefundedAmount || 0);
          setTotalNetAmount(value?.totalNetAmount || 0);
          setTotalPendingAmount(value?.totalPendingAmount || 0);
          setCurrentPage(params?.page || currentPage);
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedPaymentType,
      selectedPaymentStatus,
      selectedPaymentMethod,
      selectedEventId,
      selectedFacilityId,
      dateFilter,
      currentPage,
    ],
  );

  const handleFilterChange = <T,>(
    setter: React.Dispatch<React.SetStateAction<T | undefined>>,
    value: T | undefined,
  ) => {
    setter(value);
    setCurrentPage(1);
  };

  const dateChangeHandler = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateFilter(dates);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    fetchTransactions({ query: searchQuery, page });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        fetchTransactions({ query: value, page: 1 });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedPaymentType,
      selectedPaymentStatus,
      selectedPaymentMethod,
      selectedEventId,
      selectedFacilityId,
      dateFilter,
    ],
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    fetchTransactions({ query: searchQuery, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPaymentType,
    selectedPaymentStatus,
    selectedPaymentMethod,
    selectedEventId,
    selectedFacilityId,
    dateFilter,
  ]);

  const openLineItemsDrawer = (record: TransactionDto) => {
    setSelectedTransaction(record);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedTransaction(null);
  };

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      width: 170,
      render: (text: string) => (
        <TransactionIdText>{text || '-'}</TransactionIdText>
      ),
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      width: 180,
      render: (text: string, row: TransactionDto) => (
        <div>
          <span>{text}</span>
          {row.userPhone && (
            <PhoneWrapper>
              <IconBadge>
                <PhoneIconWrapper rotate={90} />
              </IconBadge>
              <GrayText>{row.userPhone}</GrayText>
            </PhoneWrapper>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'paymentTypeName',
      key: 'paymentTypeName',
      width: 140,
      render: (text: string) => (
        <span>{text?.replace(/([A-Z])/g, ' $1').trim() || '-'}</span>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (value: number) => <span>₹ {value?.toFixed(2)}</span>,
    },
    {
      title: 'Refund Amount',
      dataIndex: 'refundedAmount',
      key: 'refundedAmount',
      width: 110,
      render: (value: number) =>
        value > 0 ? (
          <RefundedText>₹ {value.toFixed(2)}</RefundedText>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'paymentStatusName',
      key: 'paymentStatusName',
      width: 150,
      render: (text: string) => (
        <Tag color={PAYMENT_STATUS_COLOR_MAP[text] || 'default'}>{text}</Tag>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'paymentMethodName',
      key: 'paymentMethodName',
      width: 120,
      render: (text: string) => (
        <span>{text?.replace(/([A-Z])/g, ' $1').trim() || '-'}</span>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 150,
      render: (text: string) => {
        if (!text) return '-';
        const date = moment(text);
        return (
          <VerticalAlign>
            <span>{date.format('DD/MM/YYYY')}</span>
            <GrayText>{date.format('hh:mm A')}</GrayText>
          </VerticalAlign>
        );
      },
    },
    {
      title: 'Related Entity',
      dataIndex: 'relatedEntityName',
      key: 'relatedEntityName',
      width: 180,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_: unknown, record: TransactionDto) => (
        <ViewAction onClick={() => openLineItemsDrawer(record)}>
          <EyeOutlined /> View
        </ViewAction>
      ),
    },
  ];

  // Line items drawer table columns
  const lineItemColumns = [
    {
      title: 'Type',
      dataIndex: 'paymentLineItemTypeName',
      key: 'paymentLineItemTypeName',
      width: 150,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (value: number) => <span>₹ {value?.toFixed(2)}</span>,
    },
    {
      title: 'Refund Amount',
      dataIndex: 'refundedAmount',
      key: 'refundedAmount',
      width: 110,
      render: (value: number) =>
        value > 0 ? (
          <RefundedText>₹ {value.toFixed(2)}</RefundedText>
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'paymentLineItemStatusName',
      key: 'paymentLineItemStatusName',
      width: 140,
      render: (text: string) => (
        <Tag color={LINE_ITEM_STATUS_COLOR_MAP[text] || 'default'}>{text}</Tag>
      ),
    },
  ];

  const filters: {
    options: { value: number; label: string }[];
    setter: React.Dispatch<React.SetStateAction<number | undefined>>;
    placeholder: string;
    showSearch?: boolean;
  }[] = [
    {
      options: paymentTypeOptions,
      setter: setSelectedPaymentType,
      placeholder: 'Booking Category',
    },
    {
      options: paymentStatusOptions,
      setter: setSelectedPaymentStatus,
      placeholder: 'Payment Status',
    },
    {
      options: paymentMethodOptions,
      setter: setSelectedPaymentMethod,
      placeholder: 'Payment Method',
    },
    {
      options: eventOptions,
      setter: setSelectedEventId,
      placeholder: 'Select Event',
      showSearch: true,
    },
    {
      options: facilityOptions,
      setter: setSelectedFacilityId,
      placeholder: 'Select Facility',
      showSearch: true,
    },
  ];

  const stats = [
    {
      label: 'Total Amount',
      value: totalAmount,
      color: COLORS.blue.primary,
      icon: <DollarOutlined />,
    },
    {
      label: 'Net Amount',
      value: totalNetAmount,
      color: COLORS.green.secondary,
      icon: <WalletOutlined />,
    },
    {
      label: 'Refund Amount',
      value: totalRefundedAmount,
      color: COLORS.red.primary,
      icon: <RollbackOutlined />,
    },
    {
      label: 'Pending',
      value: totalPendingAmount,
      color: COLORS.orange.primary,
      icon: <ClockCircleOutlined />,
    },
  ];

  return (
    <Container gutter={[24, 24]}>
      <Col span={24}>
        <StatsRow gutter={16}>
          {stats.map((stat) => (
            <Col span={6} key={stat.label}>
              <StatCard $borderColor={stat.color}>
                <StatIconWrapper $color={stat.color}>
                  {stat.icon}
                </StatIconWrapper>
                <StatContent>
                  <StatLabel>{stat.label}</StatLabel>
                  <StatValue $color={stat.color}>
                    ₹ {stat.value.toFixed(2)}
                  </StatValue>
                </StatContent>
              </StatCard>
            </Col>
          ))}
        </StatsRow>
      </Col>
      <Col span={24}>
        <StyledSearch
          placeholder="Search by transaction ID, user name"
          onChange={handleSearch}
          value={searchQuery}
          allowClear
          size="large"
          enterButton
        />
      </Col>
      <Col span={24}>
        <Row gutter={16}>
          {filters.map((filter, idx) => (
            <Col flex="1" key={idx}>
              <Select
                options={filter.options}
                onChange={(value) =>
                  handleFilterChange(filter.setter, value as number | undefined)
                }
                placeholder={filter.placeholder}
                style={{ width: '100%', height: '43px' }}
                allowClear
                showSearch={filter.showSearch}
                filterOption={
                  filter.showSearch
                    ? (input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                    : undefined
                }
              />
            </Col>
          ))}
          <Col flex="1">
            <StyledRangePicker
              format={'DD-MM-YYYY'}
              onChange={dateChangeHandler}
            />
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <CustomTable
          rowKey="paymentId"
          dataSource={transactions}
          columns={columns}
          pagination={{
            pageSize: PAGE_SIZE,
            current: currentPage,
            total: totalCount,
            onChange: handlePageChange,
          }}
          loading={loading}
        />
      </Col>

      {/* Line Items Drawer */}
      <Drawer
        title="Transaction Details"
        open={drawerOpen}
        onClose={closeDrawer}
        width="55vw"
      >
        {selectedTransaction && (
          <>
            <DrawerSummaryGrid>
              <SummaryItem>
                <SummaryLabel>Transaction ID</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.transactionId || '-'}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>User</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.userName}
                  {selectedTransaction.userPhone && (
                    <GrayText> ({selectedTransaction.userPhone})</GrayText>
                  )}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Payment Type</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.paymentTypeName}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Total Amount</SummaryLabel>
                <SummaryValue>
                  ₹ {selectedTransaction.amount?.toFixed(2)}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Refunded Amount</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.refundedAmount > 0 ? (
                    <RefundedText>
                      ₹ {selectedTransaction.refundedAmount.toFixed(2)}
                    </RefundedText>
                  ) : (
                    '-'
                  )}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Status</SummaryLabel>
                <SummaryValue>
                  <Tag
                    color={
                      PAYMENT_STATUS_COLOR_MAP[
                        selectedTransaction.paymentStatusName
                      ] || 'default'
                    }
                  >
                    {selectedTransaction.paymentStatusName}
                  </Tag>
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Payment Method</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.paymentMethodName}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Payment Date</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.paymentDate
                    ? moment(selectedTransaction.paymentDate).format(
                        'DD MMM YYYY, hh:mm A',
                      )
                    : '-'}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Related Entity</SummaryLabel>
                <SummaryValue>
                  {selectedTransaction.relatedEntityName || '-'}
                </SummaryValue>
              </SummaryItem>
            </DrawerSummaryGrid>

            <LineItemsHeader>
              Line Items ({selectedTransaction.lineItems?.length || 0})
            </LineItemsHeader>

            <StyledLineItemTable
              rowKey="paymentLineItemId"
              dataSource={selectedTransaction.lineItems || []}
              columns={lineItemColumns}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No line items' }}
            />
          </>
        )}
      </Drawer>
    </Container>
  );
};

// --- Styled Components ---
const Container = styled(Row)`
  padding: 8px;
`;

const StatsRow = styled(Row)`
  width: 100%;
`;

const StatCard = styled.div<{ $borderColor: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${COLORS.white.primary};
  border-radius: 8px;
  border-left: 4px solid ${(props) => props.$borderColor};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const StatIconWrapper = styled.div<{ $color: string }>`
  font-size: 20px;
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${(props) => props.$color}10;
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.secondary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const StatValue = styled.span<{ $color: string }>`
  font-size: ${TEXT_SIZES.LG}px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${(props) => props.$color};
`;

const TransactionIdText = styled.span`
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.blue.medium};
`;

const PhoneWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconBadge = styled.div`
  background-color: ${COLORS.grey.secondaryLight};
  border-radius: 10px;
  height: 18px;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhoneIconWrapper = styled(PhoneOutlined)`
  height: 10px;
  width: 10px;
`;

const GrayText = styled.span`
  color: ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.XS}px;
`;

const VerticalAlign = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRangePicker = styled(RangePicker)`
  height: 43px;
  width: 100%;
`;

const ViewAction = styled.span`
  color: ${COLORS.blue.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const RefundedText = styled.span`
  color: ${COLORS.red.primary};
`;

const DrawerSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 32px;
  margin-bottom: 24px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.span`
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.secondary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const SummaryValue = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.black.primary};
`;

const LineItemsHeader = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${COLORS.blue.primary};
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${COLORS.grey.secondaryLight};
`;

const StyledLineItemTable = styled(Table<TransactionLineItemDto>)`
  .ant-table-thead > tr > th {
    background-color: ${COLORS.blue.primary} !important;
    color: ${COLORS.white.primary} !important;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    padding: 12px 16px;
  }
`;

export default Transactions;
